import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatIDR, formatDateID } from "@/lib/format";
import { toast } from "sonner";
import { Users, Wallet, Download, Coins, Plus, Eye, FileText, Info, Search, X, Printer } from "lucide-react";

const MAROON = "#8E2A3C";
const MONTHS_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const periodLabel = (p) => { const [y, m] = (p || "").split("-"); return m ? `${MONTHS_ID[parseInt(m, 10) - 1]} ${y}` : "—"; };
const todayWIB = () => new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10);
const inp = "h-10 px-3.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-100 focus:border-[#8E2A3C]";
const ST_CHIP = {
  draft: ["Draft", "bg-slate-100 text-slate-500"],
  dicairkan_sebagian: ["Dicairkan Sebagian", "bg-amber-100 text-amber-600"],
  dibayar: ["Sudah Dicairkan", "bg-emerald-100 text-emerald-600"],
};
const initialsOf = (name) => (name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const downloadPdf = async (params, fname) => {
  try {
    const r = await api.get("/admin/payroll/slip-pdf", { params, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = fname; a.click();
    URL.revokeObjectURL(url);
    toast.success("Slip PDF diunduh");
  } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengunduh slip"); }
};

export default function AdminPayroll() {
  const [period, setPeriod] = useState(() => todayWIB().slice(0, 7));
  const [cutoff, setCutoff] = useState("");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("");
  const [data, setData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", method: "transfer", date: todayWIB() });
  const [payBusy, setPayBusy] = useState(false);

  const load = useCallback(() => {
    setData(null);
    api.get("/admin/payroll/saldo-overview", { params: { period, cutoff } })
      .then((r) => setData(r.data))
      .catch((e) => { setData(false); toast.error(e?.response?.data?.detail || "Gagal memuat payroll"); });
  }, [period, cutoff]);
  useEffect(() => { load(); }, [load]);

  const items = (data?.items || []).filter((it) => {
    if (tab === "draft" && it.status !== "draft") return false;
    if (tab === "sebagian" && it.status !== "dicairkan_sebagian") return false;
    if (tab === "lunas" && it.status !== "dibayar") return false;
    const q = search.trim().toLowerCase();
    if (q && !(`${it.name} ${it.nik}`.toLowerCase().includes(q))) return false;
    return true;
  });
  const counts = {
    all: data?.items?.length || 0,
    draft: (data?.items || []).filter((i) => i.status === "draft").length,
    sebagian: (data?.items || []).filter((i) => i.status === "dicairkan_sebagian").length,
    lunas: (data?.items || []).filter((i) => i.status === "dibayar").length,
  };
  const tot = items.reduce((a, it) => ({ saldo: a.saldo + it.saldo_now, cair: a.cair + it.paid_total, sisa: a.sisa + it.sisa_setelah }), { saldo: 0, cair: 0, sisa: 0 });

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/admin/payroll/generate", { period, cutoff: cutoff || undefined });
      toast.success(`Payroll dibuat: ${r.data.created}${r.data.skipped?.length ? ` • dilewati (sudah ada): ${r.data.skipped.length}` : ""} • batas ${formatDateID(r.data.cutoff)}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat payroll"); }
    finally { setGenerating(false); }
  };

  const openDetail = async (it) => {
    if (!it.payroll_id) return toast.info("Payroll belum dibuat untuk karyawan ini — klik Generate Payroll dulu");
    setDetailId(it.payroll_id);
    setDetail(null);
    try {
      const r = await api.get("/admin/payroll", { params: { period, employee_id: it.user_id } });
      const doc = (r.data.items || []).find((p) => p.id === it.payroll_id);
      setDetail(doc || false);
    } catch { setDetail(false); }
  };

  const openPay = (it) => {
    setPayForm({ amount: "", method: "transfer", date: todayWIB() });
    setPayModal(it);
  };

  const doPay = async () => {
    const it = payModal;
    const max = it.sisa_setelah;
    const amt = payForm.amount === "" ? max : parseInt(payForm.amount);
    if (!(amt > 0)) return toast.error("Nominal pencairan tidak valid");
    if (amt > max) return toast.error(`Nominal melebihi sisa saldo payroll (${formatIDR(max)})`);
    setPayBusy(true);
    try {
      const r = await api.post(`/admin/payroll/${it.payroll_id}/pay`, { payment_method: payForm.method, pay_date: payForm.date, amount: amt });
      toast.success(`${formatIDR(r.data.amount)} dicairkan ke ${it.name} • sisa saldo mobile ${formatIDR(r.data.sisa_saldo)}`);
      setPayModal(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Pencairan gagal"); }
    finally { setPayBusy(false); }
  };

  const stats = data?.stats;
  return (
    <div data-testid="payroll-page" className="space-y-5 pb-10">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Payroll Karyawan</h1>
          <p className="text-xs text-slate-400">Kelola saldo dan pencairan gaji karyawan tetap.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="pr-cetak-slip" onClick={() => downloadPdf({ ids: items.filter((i) => i.payroll_id).map((i) => i.payroll_id).join(",") }, `slip-payroll-${period}.pdf`)}
            className="h-10 px-4 rounded-xl border border-slate-200 text-slate-600 text-xs font-semibold flex items-center gap-1.5 hover:border-[#8E2A3C] hover:text-[#8E2A3C] transition-colors">
            <Printer className="w-3.5 h-3.5" /> Cetak Slip
          </button>
          <button data-testid="pr-generate" onClick={generate} disabled={generating}
            className="h-10 px-4 rounded-xl text-white text-xs font-bold flex items-center gap-1.5 transition-colors disabled:opacity-50" style={{ background: MAROON }}>
            <Plus className="w-3.5 h-3.5" /> {generating ? "Membuat..." : "Generate Payroll"}
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Periode</label>
          <input data-testid="pr-period" type="month" value={period} onChange={(e) => e.target.value && setPeriod(e.target.value)} className={`${inp} w-full`} />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Batas Perhitungan</label>
          <input data-testid="pr-cutoff" type="date" value={cutoff || (data?.cutoff ?? "")} onChange={(e) => setCutoff(e.target.value)} className={`${inp} w-full`} />
          {data?.cutoff && <p className="text-[9px] text-slate-400 mt-1">Mutasi sesudah tanggal ini masuk pencairan berikutnya</p>}
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Divisi</label>
          <select className={`${inp} w-full`} data-testid="pr-divisi" disabled><option>Semua Divisi</option></select>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Cari karyawan</label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-300 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input data-testid="pr-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari karyawan..." className={`${inp} w-full pl-9`} />
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {[
          { label: "Karyawan", val: stats ? stats.karyawan : "…", Icon: Users, tid: "pr-stat-karyawan" },
          { label: "Total Saldo", val: stats ? formatIDR(stats.total_saldo) : "…", Icon: Wallet, tid: "pr-stat-total-saldo" },
          { label: "Sudah Dicairkan", val: stats ? formatIDR(stats.sudah_dicairkan) : "…", Icon: Download, tid: "pr-stat-dicairkan" },
          { label: "Sisa Saldo", val: stats ? formatIDR(stats.sisa_saldo) : "…", Icon: Coins, tid: "pr-stat-sisa" },
        ].map((c) => (
          <div key={c.tid} data-testid={c.tid} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-rose-50 flex items-center justify-center text-[#8E2A3C] shrink-0"><c.Icon className="w-5 h-5" /></div>
            <div className="min-w-0">
              <div className="text-[10px] text-slate-400">{c.label}</div>
              <div className="font-display text-lg font-extrabold text-slate-900 truncate">{c.val}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        {[["", `Semua (${counts.all})`], ["draft", `Draft (${counts.draft})`], ["sebagian", `Dicairkan Sebagian (${counts.sebagian})`], ["lunas", `Sudah Dicairkan (${counts.lunas})`]].map(([v, l]) => (
          <button key={v} data-testid={`pr-tab-${v || "all"}`} onClick={() => setTab(v)}
            className={`px-4 py-2.5 text-[13px] font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === v ? "border-[#8E2A3C] text-[#8E2A3C]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>

      {/* Tabel */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50/60 border-b border-slate-100">
              <th className="text-left pl-4 pr-3 py-3 font-semibold">Karyawan</th>
              <th className="text-left px-3 py-3 font-semibold">Saldo Karyawan</th>
              <th className="text-left px-3 py-3 font-semibold">Dicairkan</th>
              <th className="text-left px-3 py-3 font-semibold">Sisa Saldo</th>
              <th className="text-left px-3 py-3 font-semibold">Status</th>
              <th className="text-right px-4 py-3 font-semibold">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {data === null && <tr><td colSpan={6} className="px-4 py-12 text-center text-xs text-slate-400">Memuat...</td></tr>}
            {data && items.map((it) => {
              const [lbl, cls] = ST_CHIP[it.status] || ["Draft", "bg-slate-100 text-slate-500"];
              return (
                <tr key={it.user_id} data-testid={`pr-row-${it.user_id}`} className="hover:bg-rose-50/20 transition-colors">
                  <td className="pl-4 pr-3 py-3">
                    <div className="flex items-center gap-2.5">
                      <span className="w-9 h-9 rounded-full bg-rose-100 text-[#8E2A3C] flex items-center justify-center text-[11px] font-extrabold shrink-0 overflow-hidden">
                        {it.photo ? <img src={it.photo} alt="" className="w-full h-full object-cover" /> : initialsOf(it.name)}
                      </span>
                      <div>
                        <div className="text-xs font-bold text-slate-800 whitespace-nowrap">{it.name}</div>
                        <div className="text-[9px] text-slate-400">NIK: {it.nik || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs font-semibold text-slate-700 whitespace-nowrap" data-testid={`pr-saldo-${it.user_id}`}>{formatIDR(it.saldo_now)}</td>
                  <td className="px-3 py-3 text-xs font-semibold text-slate-700 whitespace-nowrap">{formatIDR(it.paid_total)}</td>
                  <td className="px-3 py-3 text-xs font-semibold text-slate-700 whitespace-nowrap">{formatIDR(it.sisa_setelah)}</td>
                  <td className="px-3 py-3"><span className={`text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${cls}`}>{lbl}</span></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {it.status === "dibayar" ? (
                      <>
                        <button data-testid={`pr-detail-${it.user_id}`} onClick={() => openDetail(it)} className="h-8 px-3 rounded-lg border border-slate-200 text-slate-600 text-[11px] font-semibold inline-flex items-center gap-1 hover:border-[#8E2A3C] hover:text-[#8E2A3C] transition-colors"><Eye className="w-3 h-3" /> Detail</button>
                        <button data-testid={`pr-slip-${it.user_id}`} onClick={() => downloadPdf({ pid: it.payroll_id }, `slip-${it.name.replace(/\s+/g, "-")}.pdf`)} className="ml-2 h-8 px-3 rounded-lg border border-slate-200 text-[#8E2A3C] text-[11px] font-semibold inline-flex items-center gap-1 hover:bg-rose-50 transition-colors"><FileText className="w-3 h-3" /> Slip</button>
                      </>
                    ) : it.status === "dicairkan_sebagian" ? (
                      <>
                        <button data-testid={`pr-cair-sisa-${it.user_id}`} onClick={() => openPay(it)} className="h-8 px-3 rounded-lg text-white text-[11px] font-bold transition-colors" style={{ background: MAROON }}>Cairkan Sisa</button>
                        <button data-testid={`pr-slip-${it.user_id}`} onClick={() => downloadPdf({ pid: it.payroll_id }, `slip-${it.name.replace(/\s+/g, "-")}.pdf`)} className="ml-2 h-8 px-3 rounded-lg border border-slate-200 text-[#8E2A3C] text-[11px] font-semibold inline-flex items-center gap-1 hover:bg-rose-50 transition-colors"><FileText className="w-3 h-3" /> Slip</button>
                      </>
                    ) : (
                      <>
                        <button data-testid={`pr-detail-${it.user_id}`} onClick={() => openDetail(it)} className="h-8 px-3 rounded-lg border border-slate-200 text-slate-600 text-[11px] font-semibold inline-flex items-center gap-1 hover:border-[#8E2A3C] hover:text-[#8E2A3C] transition-colors"><Eye className="w-3 h-3" /> Cek Rincian</button>
                        <button data-testid={`pr-cairkan-${it.user_id}`} onClick={() => it.payroll_id ? openPay(it) : toast.info("Generate payroll dulu untuk karyawan ini")}
                          className="ml-2 h-8 px-3 rounded-lg text-white text-[11px] font-bold transition-colors" style={{ background: MAROON }}>Cairkan Saldo</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {data && !items.length && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-xs text-slate-400">Tidak ada karyawan pada filter ini.</td></tr>
            )}
          </tbody>
          {data && items.length > 0 && (
            <tfoot>
              <tr className="bg-rose-50/70 border-t border-rose-100">
                <td className="pl-4 pr-3 py-3 text-xs font-extrabold text-[#8E2A3C]">Total ({items.length} Karyawan)</td>
                <td className="px-3 py-3 text-xs font-extrabold text-[#8E2A3C]" data-testid="pr-total-saldo">{formatIDR(tot.saldo)}</td>
                <td className="px-3 py-3 text-xs font-extrabold text-[#8E2A3C]">{formatIDR(tot.cair)}</td>
                <td className="px-3 py-3 text-xs font-extrabold text-[#8E2A3C]" data-testid="pr-total-sisa">{formatIDR(tot.sisa)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Catatan */}
      <div className="space-y-1.5 text-[11px] text-slate-400">
        <p className="flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-px shrink-0" /> Pencairan payroll dilakukan oleh admin. Pengajuan dana karyawan melalui fitur Kasbon.</p>
        <p className="flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-px shrink-0" /> Saldo mobile berkurang setelah pencairan berhasil. Pencairan sebagian dapat dilanjutkan melalui Cairkan Sisa.</p>
      </div>

      {/* Modal Detail */}
      {detailId && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDetailId(null)}>
          <div data-testid="pr-detail-modal" className="bg-white rounded-2xl p-5 w-full max-w-md space-y-3 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h4 className="font-display font-bold text-sm text-slate-900">Rincian Payroll</h4>
              <button data-testid="pr-detail-close" onClick={() => setDetailId(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            {detail === null && <p className="text-xs text-slate-400 py-6 text-center">Memuat...</p>}
            {detail === false && <p className="text-xs text-red-500 py-6 text-center">Gagal memuat rincian.</p>}
            {detail && (
              <>
                <div className="text-xs text-slate-500">
                  <div className="font-bold text-slate-800 text-sm">{detail.user_name}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">NIK {detail.employee_id || "—"} • {detail.role_label} • Periode {formatDateID(detail.period_start)} – {formatDateID(detail.period_end)} • Batas {formatDateID(detail.cutoff)}</div>
                </div>
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 space-y-1">
                  <div className="text-[10px] font-bold text-emerald-600 tracking-wide">PENGHASILAN (rekap mutasi saldo)</div>
                  {(detail.add_components || []).map((c) => (
                    <div key={c.id} className="flex justify-between text-xs"><span className="text-slate-600">{c.name}</span><span className="font-semibold text-slate-800">+{formatIDR(c.amount)}</span></div>
                  ))}
                  {!(detail.add_components || []).length && <div className="text-[10px] text-slate-400">Tidak ada.</div>}
                </div>
                <div className="rounded-xl border border-rose-100 bg-rose-50/40 p-3 space-y-1">
                  <div className="text-[10px] font-bold text-rose-500 tracking-wide">POTONGAN</div>
                  {(detail.ded_components || []).map((c) => (
                    <div key={c.id} className="flex justify-between text-xs"><span className="text-slate-600">{c.name}</span><span className="font-semibold text-slate-800">−{formatIDR(c.amount)}</span></div>
                  ))}
                  {!(detail.ded_components || []).length && <div className="text-[10px] text-slate-400">Tidak ada.</div>}
                </div>
                <div className="rounded-xl bg-rose-50/70 border border-rose-100 px-4 py-3 space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-500">Total hak pendapatan</span><span className="font-bold text-slate-900">{formatIDR(detail.net)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Sudah dicairkan</span><span className="font-bold text-slate-900">{formatIDR(detail.paid_total || 0)}</span></div>
                  <div className="flex justify-between pt-1 border-t border-rose-100"><span className="text-slate-500">Sisa hak</span><span className="font-extrabold text-[#8E2A3C]">{formatIDR(Math.max(0, (detail.net || 0) - (detail.paid_total || 0)))}</span></div>
                </div>
                {(detail.payments || []).length > 0 && (
                  <div className="space-y-1">
                    <div className="text-[10px] font-bold text-slate-500 tracking-wide">RIWAYAT PENCAIRAN</div>
                    {detail.payments.map((pm, i) => (
                      <div key={i} className="flex justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                        <span className="text-slate-600">{formatDateID(pm.pay_date)} • {pm.method === "tunai" ? "Tunai" : "Transfer Bank"}</span>
                        <span className="font-semibold text-slate-800">{formatIDR(pm.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <button data-testid="pr-detail-slip" onClick={() => downloadPdf({ pid: detail.id }, `slip-${detail.user_name.replace(/\s+/g, "-")}.pdf`)}
                  className="w-full h-10 rounded-xl border border-slate-200 text-[#8E2A3C] text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-rose-50 transition-colors">
                  <FileText className="w-3.5 h-3.5" /> Lihat / Download Slip PDF
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Pencairan */}
      {payModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !payBusy && setPayModal(null)}>
          <div data-testid="pr-pay-modal" className="bg-white rounded-2xl p-5 w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h4 className="font-display font-bold text-sm text-slate-900">Cairkan Saldo — {payModal.name}</h4>
              <button data-testid="pr-pay-close" onClick={() => !payBusy && setPayModal(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <div className="rounded-xl bg-slate-50 px-3.5 py-2.5 text-[11px] text-slate-500 space-y-0.5">
              <div className="flex justify-between"><span>Saldo tersedia</span><span className="font-bold text-slate-800">{formatIDR(payModal.saldo_now)}</span></div>
              <div className="flex justify-between"><span>Sudah dicairkan</span><span className="font-bold text-slate-800">{formatIDR(payModal.paid_total)}</span></div>
              <div className="flex justify-between"><span>Sisa saldo payroll</span><span className="font-extrabold text-[#8E2A3C]">{formatIDR(payModal.sisa_setelah)}</span></div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Nominal Pencairan (kosongkan = seluruh sisa)</label>
              <input data-testid="pr-pay-amount" type="number" min="0" value={payForm.amount} onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))} placeholder={String(payModal.sisa_setelah)} className={`${inp} w-full`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Metode</label>
                <select data-testid="pr-pay-method" value={payForm.method} onChange={(e) => setPayForm((f) => ({ ...f, method: e.target.value }))} className={`${inp} w-full`}>
                  <option value="transfer">Transfer Bank</option>
                  <option value="tunai">Tunai</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Tanggal Bayar</label>
                <input data-testid="pr-pay-date" type="date" value={payForm.date} onChange={(e) => setPayForm((f) => ({ ...f, date: e.target.value }))} className={`${inp} w-full`} />
              </div>
            </div>
            <p className="text-[10px] text-slate-400">Saldo mobile karyawan berkurang tepat setelah pencairan ini berhasil dan tercatat di riwayat mutasi sebagai "Pencairan Payroll". Saldo terbaru diperiksa ulang sebelum diproses.</p>
            <div className="flex gap-2 pt-1">
              <button data-testid="pr-pay-cancel" onClick={() => setPayModal(null)} disabled={payBusy} className="flex-1 h-10 rounded-xl border border-slate-200 text-slate-500 text-xs font-semibold hover:border-slate-300 disabled:opacity-50">Batal</button>
              <button data-testid="pr-pay-confirm" onClick={doPay} disabled={payBusy}
                className="flex-1 h-10 rounded-xl text-white text-xs font-bold transition-colors disabled:opacity-50" style={{ background: MAROON }}>
                {payBusy ? "Memproses..." : `Cairkan ${payForm.amount ? formatIDR(parseInt(payForm.amount) || 0) : formatIDR(payModal.sisa_setelah)}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
